/* 2B CODE-SEARCH-01. Source-backed separator normalization, never SKU edits.
   Native predictive markup and Boost results remain authoritative.
   Candidate discovery is bounded, locale-aware and publicly searchable only. */
(() => {
  'use strict';
  if (window.B2CodeSearch) { window.B2CodeSearch.refresh(); return; }
  const VERSION = '2026-10-23.2';
  const cache = new Map(), requests = new WeakMap();
  const normalize = value => String(value || '').normalize('NFKC').toUpperCase().replace(/[\s.\/\-\u2010-\u2015]/g, '');
  const codeLike = value => {
    const raw = String(value || '').trim(), key = normalize(raw);
    return raw.length <= 64 && /^[A-Z0-9\s.\/\-\u2010-\u2015]+$/i.test(raw) &&
      /^[A-Z0-9]{4,40}$/.test(key) && /[A-Z]/.test(key) && /\d/.test(key);
  };
  const root = () => window.Shopify?.routes?.root || '/';
  const searchPath = () => window.PXUTheme?.routes?.search_url || root() + 'search';
  const withPreview = url => {
    const id = new URL(location.href).searchParams.get('preview_theme_id');
    if (id) url.searchParams.set('preview_theme_id', id);
    return url;
  };
  const prefixOf = (sku, length) => {
    let result = '', count = 0;
    for (const char of sku) {
      result += char;
      if (/[A-Z0-9]/i.test(char) && ++count === length) return result.trim();
    }
    return '';
  };
  const selectCanonical = (raw, skus) => {
    const key = normalize(raw);
    let matches = skus.filter(sku => normalize(sku).startsWith(key));
    const exact = matches.filter(sku => normalize(sku) === key);
    if (exact.length) matches = exact;
    const choices = new Set(matches.map(sku => prefixOf(sku, key.length).toUpperCase()).filter(Boolean));
    if (choices.size !== 1) return raw;
    const canonical = choices.values().next().value;
    return canonical.toUpperCase() === raw.toUpperCase() ? raw : canonical;
  };
  // Discover every hyphen/dot/slash layout in this short prefix. These are
  // lookup candidates only; the returned real SKU is the sole rewrite authority.
  const discoveryQuery = prefix => {
    let layouts = [prefix[0]];
    for (const character of prefix.slice(1)) {
      layouts = layouts.flatMap(part => ['', '-', '.', '/'].map(separator => part + separator + character));
    }
    return layouts.map(part => 'variants.sku:' + part + '*').join(' OR ');
  };
  async function loadCandidates(prefix) {
    const cacheKey = root() + '|' + (window.Shopify?.country || '') + '|' + prefix;
    const old = cache.get(cacheKey);
    if (old && Date.now() - old.at < 120000) return old.promise;
    if (cache.size >= 24) cache.delete(cache.keys().next().value);
    const promise = (async () => {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 3500);
      try {
        const skus = [];
        for (let page = 1; page <= 3; page += 1) {
          const url = withPreview(new URL(searchPath(), location.origin));
          url.searchParams.set('type', 'product');
          url.searchParams.set('view', 'b2-code-candidates');
          url.searchParams.set('q', discoveryQuery(prefix));
          url.searchParams.set('page', String(page));
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Candidate HTTP ' + response.status);
          const text = await response.text();
          if (text.length > 150000) throw new Error('Candidate size limit');
          const data = JSON.parse(text);
          if (data.schema !== 1 || !Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages > 3 || data.total > 750) throw new Error('Incomplete candidate set');
          for (const product of data.products) {
            if (!Array.isArray(product.skus)) throw new Error('Invalid SKU set');
            for (const sku of product.skus) if (typeof sku === 'string' && sku.length <= 80 && /^[A-Z0-9\s.\/\-]+$/i.test(sku)) skus.push(sku);
          }
          if (page >= data.pages) return [...new Set(skus)];
        }
        throw new Error('Candidate pagination bound');
      } finally { clearTimeout(timer); }
    })();
    cache.set(cacheKey, { at: Date.now(), promise });
    promise.catch(() => { if (cache.get(cacheKey)?.promise === promise) cache.delete(cacheKey); });
    return promise;
  }
  async function resolve(raw) {
    raw = String(raw || '').trim();
    if (!codeLike(raw)) return raw;
    try {
      const key = normalize(raw);
      for (const length of [3, 2]) {
        const skus = await loadCandidates(key.slice(0, length));
        if (skus.some(sku => normalize(sku).startsWith(key))) return selectCanonical(raw, skus);
      }
    } catch (_) { /* Preserve original search on timeout, ambiguity or invalid data. */ }
    return raw;
  }
  function installPredictive() {
    const native = window.predictiveSearch;
    if (!native || native.__b2CodeSearchVersion === VERSION || typeof native._fetchResults !== 'function') return;
    const original = native._fetchResults;
    native._fetchResults = async function (term, form, results, container) {
      if (form.classList.contains('search-form--blog')) return original.call(this, term, form, results, container);
      requests.get(form)?.abort();
      const controller = new AbortController(); requests.set(form, controller);
      const input = form.querySelector('[data-search-input]');
      const current = () => !controller.signal.aborted && form.isConnected && input?.value.trim() === term && requests.get(form) === controller;
      container.setAttribute('data-loading', 'true');
      const timer = setTimeout(() => controller.abort(), 8000);
      try {
        const canonical = await resolve(term);
        if (!current()) return;
        const url = withPreview(new URL(window.PXUTheme.routes.predictive_search_url, location.origin));
        url.searchParams.set('q', canonical); url.searchParams.set('section_id', 'predictive-search');
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Predictive HTTP ' + response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive markup missing');
        if (current()) this._renderResults(section.innerHTML, results, container);
      } catch (_) {
        if (form.isConnected && input?.value.trim() === term && requests.get(form) === controller) {
          const button = document.createElement('button'); button.type = 'submit';
          button.className = 'predictive-search__search-button global-button';
          button.dataset.searchButton = '';
          button.textContent = (document.documentElement.lang.startsWith('it') ? 'Cerca ' : 'Search ') + '“' + term + '”';
          results.replaceChildren(button);
        }
      } finally {
        clearTimeout(timer);
        if (requests.get(form) === controller) container.setAttribute('data-loading', 'false');
      }
    };
    native.__b2CodeSearchVersion = VERSION;
  }
  let interaction = 0, navigation = 0, lastLocation = '';
  document.addEventListener('input', event => {
    if (!event.target.matches('.search-form [data-search-input]')) return;
    interaction += 1; installPredictive();
    const form = event.target.closest('form'); requests.get(form)?.abort();
    // Hide stale results immediately; Turbo's debounced handler supplies the next response.
    const results = form.querySelector('[data-predictive-search-results]');
    if (results) results.replaceChildren();
  }, true);
  document.addEventListener('focusin', installPredictive, true);
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.search-form') || form.classList.contains('search-form--blog')) return;
    const input = form.querySelector('[data-search-input]'), raw = input?.value.trim();
    if (!codeLike(raw) || (form.target && form.target !== '_self')) return;
    const target = new URL(form.action, location.origin);
    if (target.origin !== location.origin || target.pathname !== new URL(searchPath(), location.origin).pathname) return;
    event.preventDefault();
    const ownNavigation = ++navigation, startLocation = location.href, epoch = interaction;
    const canonical = await resolve(raw);
    if (!form.isConnected || input.value.trim() !== raw || navigation !== ownNavigation || location.href !== startLocation || epoch !== interaction) return;
    target.search = new URLSearchParams(new FormData(form)).toString();
    target.searchParams.set('q', canonical); withPreview(target);
    location.assign(target.href);
  }, true);
  async function normalizeSearchLocation() {
    const before = location.href;
    if (before === lastLocation) return;
    lastLocation = before;
    const url = new URL(before);
    if (url.pathname !== new URL(searchPath(), location.origin).pathname || url.searchParams.has('view') || url.searchParams.has('section_id') || url.searchParams.get('type') === 'article') return;
    const raw = url.searchParams.get('q');
    if (!codeLike(raw)) return;
    const epoch = interaction, nav = navigation;
    const canonical = await resolve(raw);
    if (canonical !== raw && location.href === before && interaction === epoch && navigation === nav) {
      url.searchParams.set('q', canonical);
      location.replace(url.href);
    }
  }
  function refresh() { installPredictive(); normalizeSearchLocation(); }
  window.B2CodeSearch = { version: VERSION, resolve, normalize, codeLike, selectCanonical, refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
  window.addEventListener('pageshow', refresh);
  window.addEventListener('load', () => {
    refresh();
    if (window.InstantClick?.on) window.InstantClick.on('change', refresh);
  }, { once: true });
})();
